import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { campusesApi } from '@/services/campuses.service';
import { uploadImage } from '@/services/upload.service';
import type {
  CampusBannerInput,
  CampusFormValues,
  CampusListItem,
  CampusListResult,
  CampusStatus,
} from '@/types/campus.types';

const PAGE_SIZE = 12;

const CAMPUSES_QUERY_KEY = 'campuses';

const DEFAULT_BANNER_LABEL = 'FROM SHOUTT TEAM';

/** Pull the server's envelope error message off a thrown request error. */
const extractApiError = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message ?? null;
};

type ModalState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; campus: CampusListItem }
  | { kind: 'delete'; campus: CampusListItem };

const EMPTY_BANNER: CampusBannerInput = {
  enabled: true,
  label: DEFAULT_BANNER_LABEL,
  message: '',
  imageUrl: '',
  tapOpensContact: true,
  contactText: '',
};

const EMPTY_FORM: CampusFormValues = {
  name: '',
  city: '',
  state: '',
  emailDomains: [],
  departments: [],
  status: 'active',
  banner: EMPTY_BANNER,
};

const formFromCampus = (campus: CampusListItem): CampusFormValues => ({
  name: campus.name,
  city: campus.city ?? '',
  state: campus.state ?? '',
  emailDomains: campus.emailDomains,
  // Preserve ids so the server can tell renames from removals on update.
  departments: campus.departments.map((d) => ({ id: d.id, name: d.name })),
  status: campus.status,
  banner: {
    enabled: campus.banner.enabled,
    label: campus.banner.label,
    message: campus.banner.message,
    imageUrl: campus.banner.imageUrl ?? '',
    tapOpensContact: campus.banner.tapOpensContact,
    contactText: campus.banner.contactText,
  },
});

interface UseCampusesPageResult {
  campuses: CampusListItem[];
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage: string | null;
  search: string;
  statusFilter: 'all' | CampusStatus;
  modalState: ModalState;
  formValues: CampusFormValues;
  domainInput: string;
  deptInput: string;
  formError: string | null;
  isSaving: boolean;
  isUploadingBanner: boolean;
  bannerUploadError: string | null;
  isDeleting: boolean;
  togglingStatusId: string | null;
  onChangeSearch: (value: string) => void;
  onChangeStatus: (status: 'all' | CampusStatus) => void;
  onChangePage: (page: number) => void;
  onOpenAdd: () => void;
  onOpenEdit: (campus: CampusListItem) => void;
  onOpenDelete: (campus: CampusListItem) => void;
  onCloseModal: () => void;
  onChangeFormField: (
    field: keyof CampusFormValues,
    value: string | string[] | CampusStatus,
  ) => void;
  onChangeDomainInput: (value: string) => void;
  onAddDomain: () => void;
  onRemoveDomain: (domain: string) => void;
  onChangeDeptInput: (value: string) => void;
  onAddDept: () => void;
  onRemoveDept: (index: number) => void;
  onChangeBannerField: (field: keyof CampusBannerInput, value: string | boolean) => void;
  onUploadBannerImage: (file: File) => void;
  onRemoveBannerImage: () => void;
  onSubmitForm: () => void;
  onConfirmDelete: () => void;
  onToggleStatus: (campus: CampusListItem) => void;
}

export const useCampusesPage = (): UseCampusesPageResult => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | CampusStatus>('all');
  const [page, setPage] = useState(1);
  const [modalState, setModalState] = useState<ModalState>({ kind: 'none' });
  const [formValues, setFormValues] = useState<CampusFormValues>(EMPTY_FORM);
  const [domainInput, setDomainInput] = useState('');
  const [deptInput, setDeptInput] = useState('');
  const [togglingStatusId, setTogglingStatusId] = useState<string | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const listQuery = useQuery<CampusListResult>({
    queryKey: [CAMPUSES_QUERY_KEY, { search: debouncedSearch, status: statusFilter, page, limit: PAGE_SIZE }],
    queryFn: () =>
      campusesApi.list({
        search: debouncedSearch || undefined,
        status: statusFilter,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (previous) => previous,
  });

  const invalidateCampuses = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: [CAMPUSES_QUERY_KEY] }).then(() => undefined);

  const createMutation = useMutation<CampusListItem, unknown, CampusFormValues>({
    mutationFn: (data) => campusesApi.create(data),
    onSuccess: () => {
      setModalState({ kind: 'none' });
      void invalidateCampuses();
    },
  });

  const updateMutation = useMutation<CampusListItem, unknown, { id: string; data: Partial<CampusFormValues> }>({
    mutationFn: ({ id, data }) => campusesApi.update(id, data),
    onSuccess: () => {
      setModalState({ kind: 'none' });
      void invalidateCampuses();
    },
  });

  const statusMutation = useMutation<CampusListItem, unknown, { id: string; status: CampusStatus }>({
    mutationFn: ({ id, status }) => campusesApi.update(id, { status }),
    onMutate: ({ id }) => setTogglingStatusId(id),
    onSettled: () => {
      setTogglingStatusId(null);
      void invalidateCampuses();
    },
  });

  const deleteMutation = useMutation<void, unknown, string>({
    mutationFn: (id) => campusesApi.delete(id),
    onSuccess: () => {
      setModalState({ kind: 'none' });
      void invalidateCampuses();
    },
  });

  const resetAndSet =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setter(value);
      setPage(1);
    };

  const onAddDomain = (): void => {
    const domain = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!domain || formValues.emailDomains.includes(domain)) {
      setDomainInput('');
      return;
    }
    setFormValues((prev) => ({ ...prev, emailDomains: [...prev.emailDomains, domain] }));
    setDomainInput('');
  };

  const onAddDept = (): void => {
    const name = deptInput.trim();
    const exists = formValues.departments.some(
      (d) => d.name.toLowerCase() === name.toLowerCase(),
    );
    if (!name || exists) {
      setDeptInput('');
      return;
    }
    setFormValues((prev) => ({ ...prev, departments: [...prev.departments, { name }] }));
    setDeptInput('');
  };

  const MAX_BANNER_BYTES = 5 * 1024 * 1024;

  const onUploadBannerImage = (file: File): void => {
    setBannerUploadError(null);
    if (!file.type.startsWith('image/')) {
      setBannerUploadError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_BANNER_BYTES) {
      setBannerUploadError('Image must be under 5 MB.');
      return;
    }
    setIsUploadingBanner(true);
    uploadImage('campus-banners', file)
      .then((url) => {
        setFormValues((prev) => ({ ...prev, banner: { ...prev.banner, imageUrl: url } }));
      })
      .catch(() => setBannerUploadError('Upload failed. Please try again.'))
      .finally(() => setIsUploadingBanner(false));
  };

  const onSubmitForm = (): void => {
    if (modalState.kind === 'add') {
      createMutation.mutate(formValues);
    } else if (modalState.kind === 'edit') {
      updateMutation.mutate({ id: modalState.campus.id, data: formValues });
    }
  };

  const onConfirmDelete = (): void => {
    if (modalState.kind === 'delete') {
      deleteMutation.mutate(modalState.campus.id);
    }
  };

  return {
    campuses: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    page: listQuery.data?.page ?? page,
    totalPages: listQuery.data?.totalPages ?? 1,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    search,
    statusFilter,
    modalState,
    formValues,
    domainInput,
    deptInput,
    formError: extractApiError(createMutation.error) ?? extractApiError(updateMutation.error),
    isSaving: createMutation.isPending || updateMutation.isPending,
    isUploadingBanner,
    bannerUploadError,
    isDeleting: deleteMutation.isPending,
    togglingStatusId,

    onChangeSearch: resetAndSet(setSearch),
    onChangeStatus: resetAndSet(setStatusFilter),
    onChangePage: setPage,

    onOpenAdd: () => {
      setFormValues(EMPTY_FORM);
      setDomainInput('');
      setDeptInput('');
      setBannerUploadError(null);
      createMutation.reset();
      updateMutation.reset();
      setModalState({ kind: 'add' });
    },
    onOpenEdit: (campus) => {
      setFormValues(formFromCampus(campus));
      setDomainInput('');
      setDeptInput('');
      setBannerUploadError(null);
      createMutation.reset();
      updateMutation.reset();
      setModalState({ kind: 'edit', campus });
    },
    onOpenDelete: (campus) => setModalState({ kind: 'delete', campus }),
    onCloseModal: () => setModalState({ kind: 'none' }),

    onChangeFormField: (field, value) =>
      setFormValues((prev) => ({ ...prev, [field]: value })),
    onChangeDomainInput: setDomainInput,
    onAddDomain,
    onRemoveDomain: (domain) =>
      setFormValues((prev) => ({
        ...prev,
        emailDomains: prev.emailDomains.filter((d) => d !== domain),
      })),
    onChangeDeptInput: setDeptInput,
    onAddDept,
    onRemoveDept: (index) =>
      setFormValues((prev) => ({
        ...prev,
        departments: prev.departments.filter((_, i) => i !== index),
      })),
    onChangeBannerField: (field, value) =>
      setFormValues((prev) => ({ ...prev, banner: { ...prev.banner, [field]: value } })),
    onUploadBannerImage,
    onRemoveBannerImage: () =>
      setFormValues((prev) => ({ ...prev, banner: { ...prev.banner, imageUrl: '' } })),
    onSubmitForm,
    onConfirmDelete,
    onToggleStatus: (campus) =>
      statusMutation.mutate({
        id: campus.id,
        status: campus.status === 'active' ? 'paused' : 'active',
      }),
  };
};
